// Tennis bet-product registry — the single source of truth for every tennis
// product that has its own standalone screen (mirrors the football per-product
// pages). Each is a stakeable product: a screen surfaces its suggested acca /
// picks to stake. Routes, the products hub, and the sidebar are generated from
// this list. Honest-empty until the TIM engine's data flows.
import type { TierGate } from "../../../auth/tier-gates.ts";

export type ProductFamily = "volume" | "layer";
// How the suggested output is shaped on the screen:
//   acca   — one combined accumulator slip to stake
//   board  — a flat ranked list of standalone picks
//   card   — a fixed multi-slot card (one per market)
//   tiers  — a small tiered set of picks
//   single — one rolling pick
//   alert  — event-driven alerts
export type ProductFormat = "acca" | "board" | "card" | "tiers" | "single" | "alert";

// Sidebar sub-section a product lives under — keeps the Tennis nav grouped by
// feature type instead of one long flat list (mirrors Football's labelled
// groups). Append a new group here + in PRODUCT_NAV_GROUPS to extend.
export type ProductNavGroup = "daily" | "jackpot" | "live" | "layer";

// Display order + labels for the grouped Tennis sidebar (and product hub).
export const PRODUCT_NAV_GROUPS: readonly {
  readonly group: ProductNavGroup;
  readonly label: string;
}[] = [
  { group: "daily", label: "Daily Plays" },
  { group: "jackpot", label: "Jackpot & Accas" },
  { group: "live", label: "Live & Tracked" },
  { group: "layer", label: "Intelligence Layers" },
];

export interface TennisProduct {
  readonly slug: string;
  readonly name: string;
  readonly family: ProductFamily;
  readonly format: ProductFormat;
  readonly navGroup: ProductNavGroup;
  readonly tier: TierGate;
  readonly tierLabel: string;
  readonly tagline: string;
  readonly what: string; // one-line description of the stakeable output
}

export const productsByNavGroup = (group: ProductNavGroup): TennisProduct[] =>
  TENNIS_PRODUCTS.filter((p) => p.navGroup === group);

export const TENNIS_PRODUCTS: readonly TennisProduct[] = [
  // ── Daily Plays (every-day volume over the day's BET pool) ─────────
  {
    slug: "full-slate",
    name: "The Full Slate",
    family: "volume",
    format: "board",
    navGroup: "daily",
    tier: "sika_pro",
    tierLabel: "Starter+",
    tagline: "Every qualifying pick",
    what: "A flat, edge-ranked board of every qualifying BET — 40–50+ standalone picks on a permitting day.",
  },
  {
    slug: "court-card",
    name: "The Court Card",
    family: "volume",
    format: "card",
    navGroup: "daily",
    tier: "sika_pro",
    tierLabel: "Starter+",
    tagline: "5 markets, 5 picks",
    what: "A structured daily card — one BET per market (set win, games, total sets, first set, correct score).",
  },
  {
    slug: "break-point",
    name: "The Break Point",
    family: "volume",
    format: "tiers",
    navGroup: "daily",
    tier: "sika_pro",
    tierLabel: "Starter+",
    tagline: "3 tiered daily picks",
    what: "Primary, Secondary and Long Shot — one pick per edge band, every active day.",
  },
  {
    slug: "momentum-chain",
    name: "The Momentum Chain",
    family: "volume",
    format: "single",
    navGroup: "daily",
    tier: "sika_pro",
    tierLabel: "Starter+",
    tagline: "Daily rolling single",
    what: "One pick a day from a player on an ascending form curve — year-round, with honest Rest Days.",
  },
  {
    slug: "challenger-slate",
    name: "The Challenger Board",
    family: "volume",
    format: "board",
    navGroup: "daily",
    tier: "phantom_pro",
    tierLabel: "Pro+",
    tagline: "Challenger-tour coverage",
    what: "Model reads on ATP Challenger singles wherever the market posts a price — wider coverage beyond the Grand Slam board, flagged lower-confidence.",
  },
  // ── Jackpot & Accas (combined slips + season/event passes) ─────────
  {
    slug: "powerball",
    name: "Powerball 10",
    family: "volume",
    format: "acca",
    navGroup: "jackpot",
    tier: "sika_pro",
    tierLabel: "Starter+",
    tagline: "10-leg jackpot accumulator",
    what: "The day's top 10 BET signals packaged into one jackpot slip.",
  },
  {
    slug: "mega25",
    name: "Mega 25",
    family: "volume",
    format: "acca",
    navGroup: "jackpot",
    tier: "sika_pro",
    tierLabel: "Starter+",
    tagline: "25-leg mega accumulator",
    what: "25 qualifying signals across the full draw — BET + high-edge MONITOR, one per match.",
  },
  {
    slug: "mixed-mega",
    name: "Mixed Mega",
    family: "volume",
    format: "acca",
    navGroup: "jackpot",
    tier: "sika_pro",
    tierLabel: "Starter+",
    tagline: "45-leg mixed accumulator",
    what: "The maximum-volume accumulator — multiple markets per match, every qualifying edge across two match-days.",
  },
  {
    slug: "clay-verdict",
    name: "The Clay Court Verdict",
    family: "volume",
    format: "acca",
    navGroup: "jackpot",
    tier: "phantom_pro",
    tierLabel: "Pro+",
    tagline: "5 pre-event predictions",
    what: "Five highest-conviction pre-event calls per event, compounding across the clay season.",
  },
  {
    slug: "tour-passport",
    name: "The Tour Passport",
    family: "volume",
    format: "acca",
    navGroup: "jackpot",
    tier: "desk_pro",
    tierLabel: "Premium",
    tagline: "Premium annual pass",
    what: "Five picks per event across seven premium events — the annual subscription product.",
  },
  // ── Live & Tracked (in-play alerts + per-round commitments) ────────
  {
    slug: "gauntlet",
    name: "The Gauntlet",
    family: "volume",
    format: "single",
    navGroup: "live",
    tier: "free",
    tierLabel: "Free+",
    tagline: "One player, every round",
    what: "One committed player tracked R1→Final — one confirmed pick per round.",
  },
  {
    slug: "decider",
    name: "The Decider",
    family: "volume",
    format: "alert",
    navGroup: "live",
    tier: "phantom_pro",
    tierLabel: "Pro+",
    tagline: "Deciding-set alerts",
    what: "Fires at genuine deciding-set moments — the seconds before the live book adjusts.",
  },
  // ── Intelligence layers as bet products ──────────────────────────
  {
    slug: "clay-trap",
    name: "Clay Trap Detector",
    family: "layer",
    format: "board",
    navGroup: "layer",
    tier: "free",
    tierLabel: "Free+",
    tagline: "Fade the trapped seed",
    what: "Set-win and total-sets picks against top seeds whose clay readiness lags their ranking.",
  },
  {
    slug: "draw-mapper",
    name: "Draw Advantage Mapper",
    family: "layer",
    format: "board",
    navGroup: "layer",
    tier: "sika_pro",
    tierLabel: "Starter+",
    tagline: "Back the beneficiary",
    what: "Picks built around the players the draw has handed the most favourable path.",
  },
  {
    slug: "ssvi",
    name: "Surface Switch Index",
    family: "layer",
    format: "board",
    navGroup: "layer",
    tier: "phantom_pro",
    tierLabel: "Pro+",
    tagline: "Fade the surface-fatigued",
    what: "Set picks against players carrying hard-court fatigue into their opening clay matches.",
  },
  {
    slug: "momentum",
    name: "Momentum Tracker",
    family: "layer",
    format: "board",
    navGroup: "layer",
    tier: "sika_pro",
    tierLabel: "Starter+",
    tagline: "Ride ascending form",
    what: "Picks on players whose match-quality trajectory is outrunning their pre-tournament price.",
  },
  {
    slug: "set-swing",
    name: "Set Swing Predictor",
    family: "layer",
    format: "alert",
    navGroup: "layer",
    tier: "phantom_pro",
    tierLabel: "Pro+",
    tagline: "Live set-boundary swings",
    what: "Live re-priced picks the moment a set completes — before the book catches up.",
  },
  {
    slug: "fatigue-cliff",
    name: "Fatigue Cliff Alert",
    family: "layer",
    format: "alert",
    navGroup: "layer",
    tier: "phantom_pro",
    tierLabel: "Pro+",
    tagline: "Fade the cliff",
    what: "Fades on players who've crossed a personal fatigue threshold before their next match.",
  },
  {
    slug: "h2h-revisionist",
    name: "H2H Revisionist",
    family: "layer",
    format: "board",
    navGroup: "layer",
    tier: "free",
    tierLabel: "Free+",
    tagline: "The clay H2H that matters",
    what: "Set and total-sets picks from the clay-only, set-level head-to-head the headline record hides.",
  },
];

export const productBySlug = (slug: string): TennisProduct | undefined =>
  TENNIS_PRODUCTS.find((p) => p.slug === slug);
